<script setup lang="ts">
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import type { MeilisearchSearchResponse } from '@meilisearch/instant-meilisearch';
import { onClickOutside, useDebounceFn } from '@vueuse/core';
import AppSearchField from '../base/AppSearchField.vue';

type ProductSearchHit = {
  objectID: string;
  id?: number | string;
  title?: string;
  merchant_name?: string | null;
  category_label?: string | null;
  sku?: string | null;
  price_in_cents?: number | string | null;
  image_url?: string | null;
};

type HighlightPart = {
  text: string;
  highlighted: boolean;
};

type InstantSearchClient = ReturnType<typeof instantMeiliSearch>['searchClient'];
type SearchResponse = {
  results: Array<MeilisearchSearchResponse<ProductSearchHit>>;
};
type SearchFunction = (
  requests: Array<{
    indexName: string;
    params: Record<string, unknown>;
  }>
) => Promise<SearchResponse>;

const SEARCH_HITS_PER_PAGE = 12;
const DEFAULT_MAX_PAGES = 50;
const SEARCH_VISIBLE_PAGE_BUTTONS = 5;

const root = ref<HTMLElement | null>(null);
const panel = ref<HTMLElement | null>(null);
const search = ref('');
const hits = ref<ProductSearchHit[]>([]);
const currentPage = ref(1);
const totalPages = ref(0);
const totalResults = ref(0);
const loading = ref(false);
const focused = ref(false);
const error = ref(false);
const activeIndex = ref(-1);
const searchClient = shallowRef<InstantSearchClient | null>(null);
const productsIndex = ref('');
const hitsPerPage = ref(SEARCH_HITS_PER_PAGE);
const maxPages = ref(DEFAULT_MAX_PAGES);
const clientReady = ref(false);
let requestId = 0;
let previousBodyOverflow = '';
let previousDocumentOverflow = '';
let bodyScrollLocked = false;

const hasQuery = computed(() => search.value.trim().length > 0);
const isOpen = computed(() => focused.value && hasQuery.value);
const activeHit = computed(() => (activeIndex.value >= 0 ? hits.value[activeIndex.value] : undefined));
const visibleTotalPages = computed(() => Math.min(totalPages.value, maxPages.value));
const showPagination = computed(() => visibleTotalPages.value > 1);
const resultSummary = computed(() => {
  if (!hits.value.length) {
    return '';
  }

  const start = (currentPage.value - 1) * hitsPerPage.value + 1;
  const fallbackTotal = start + hits.value.length - 1;
  const total = totalResults.value || fallbackTotal;
  const end = Math.min(fallbackTotal, total);

  return `Showing ${formatCount(start)}-${formatCount(end)} of ${formatCount(total)} results`;
});
const { isMobile } = useScreenSize();
const highlightTokens = computed(() => {
  const tokens = search.value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token.toLowerCase());

  return [...new Set(tokens)].sort((a, b) => b.length - a.length);
});

const loadSearchClient = async () => {
  if (searchClient.value) {
    return;
  }

  const { loadSearchConfig } = await import('@/search/searchConfig');
  const config = await loadSearchConfig();
  productsIndex.value = config.products_index;
  hitsPerPage.value = SEARCH_HITS_PER_PAGE;
  maxPages.value = toPositiveInteger(config.pagination?.max_pages, DEFAULT_MAX_PAGES);
  searchClient.value = instantMeiliSearch(config.host, config.search_key, {
    placeholderSearch: false,
    finitePagination: true,
    primaryKey: 'id',
    meiliSearchParams: {
      attributesToRetrieve: [
        'id',
        'title',
        'sku',
        'merchant_name',
        'category_label',
        'image_url',
        'price_in_cents',
      ],
    },
  }).searchClient;
  clientReady.value = true;
};

const runSearch = async () => {
  const query = search.value.trim();
  const currentRequest = ++requestId;

  if (!query) {
    hits.value = [];
    totalPages.value = 0;
    totalResults.value = 0;
    activeIndex.value = -1;
    loading.value = false;
    error.value = false;
    return;
  }

  error.value = false;

  try {
    if (!clientReady.value && !hits.value.length) {
      loading.value = true;
    }

    await loadSearchClient();

    const searchProducts = searchClient.value?.search as SearchFunction | undefined;
    const page = Math.max(0, Math.min(currentPage.value, maxPages.value) - 1);
    const response = await searchProducts?.([
      {
        indexName: productsIndex.value,
        params: {
          query,
          hitsPerPage: hitsPerPage.value,
          page,
        },
      },
    ]);

    if (currentRequest !== requestId) {
      return;
    }

    const result = response?.results?.[0];
    hits.value = result?.hits ?? [];
    totalResults.value = toNonNegativeInteger(result?.nbHits, hits.value.length);
    totalPages.value = getTotalPages(result?.nbPages, totalResults.value);
    activeIndex.value = hits.value.length ? 0 : -1;
  } catch {
    if (currentRequest === requestId) {
      error.value = true;
      hits.value = [];
      totalPages.value = 0;
      totalResults.value = 0;
      activeIndex.value = -1;
    }
  } finally {
    if (currentRequest === requestId) {
      loading.value = false;
    }
  }
};

const debouncedSearch = useDebounceFn(runSearch, 180);

watch(search, () => {
  requestId += 1;
  error.value = false;

  if (!hasQuery.value) {
    hits.value = [];
    currentPage.value = 1;
    totalPages.value = 0;
    totalResults.value = 0;
    activeIndex.value = -1;
    loading.value = false;
  } else if (currentPage.value !== 1) {
    currentPage.value = 1;
  }

  debouncedSearch();
});

watch(isOpen, (open) => {
  setBodyScrollLocked(open);
});

onMounted(() => {
  loadSearchClient().catch(() => {
    error.value = true;
  });
});

onBeforeUnmount(() => {
  setBodyScrollLocked(false);
});

onClickOutside(
  root,
  () => {
    focused.value = false;
  },
  { ignore: [panel] }
);

function productId(item: ProductSearchHit) {
  return item.id ?? item.objectID;
}

function productPath(item: ProductSearchHit) {
  return `/browse/products/${productId(item)}`;
}

function productImage(item: ProductSearchHit) {
  return item.image_url || '/logo.png';
}

function productPrice(item: ProductSearchHit) {
  const cents = toNumber(item.price_in_cents);

  if (cents === null) {
    return '';
  }

  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(cents / 100);
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightedParts(value?: string | number | null): HighlightPart[] {
  const text = value == null ? '' : String(value);
  const tokens = highlightTokens.value;

  if (!text || !tokens.length) {
    return [{ text, highlighted: false }];
  }

  const regex = new RegExp(tokens.map(escapeRegExp).join('|'), 'gi');
  const parts: HighlightPart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, match.index), highlighted: false });
    }

    parts.push({ text: match[0], highlighted: true });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex), highlighted: false });
  }

  return parts;
}

function toNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function toPositiveInteger(value: unknown, fallback: number) {
  const number = toNumber(value);

  if (number === null || number < 1) {
    return fallback;
  }

  return Math.floor(number);
}

function toNonNegativeInteger(value: unknown, fallback: number) {
  const number = toNumber(value);

  if (number === null || number < 0) {
    return fallback;
  }

  return Math.floor(number);
}

function getTotalPages(responsePages: unknown, resultCount: number) {
  const pagesFromResponse = toPositiveInteger(responsePages, 0);
  const pagesFromHits = Math.ceil(resultCount / hitsPerPage.value);

  return Math.min(Math.max(pagesFromResponse, pagesFromHits), maxPages.value);
}

function formatCount(value: number) {
  return new Intl.NumberFormat('en-AU').format(value);
}

function setBodyScrollLocked(locked: boolean) {
  if (!import.meta.client) {
    return;
  }

  const { body, documentElement } = document;

  if (locked && !bodyScrollLocked) {
    previousBodyOverflow = body.style.overflow;
    previousDocumentOverflow = documentElement.style.overflow;
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    bodyScrollLocked = true;
    return;
  }

  if (!locked && bodyScrollLocked) {
    body.style.overflow = previousBodyOverflow;
    documentElement.style.overflow = previousDocumentOverflow;
    bodyScrollLocked = false;
  }
}

function moveActive(delta: number) {
  if (!hits.value.length) {
    return;
  }

  activeIndex.value = (activeIndex.value + delta + hits.value.length) % hits.value.length;
}

async function selectHit(item?: ProductSearchHit) {
  const selected = item ?? activeHit.value ?? hits.value[0];

  if (!selected) {
    return;
  }

  const selectedQuery = search.value.trim();
  focused.value = false;
  search.value = '';
  hits.value = [];
  currentPage.value = 1;
  totalPages.value = 0;
  totalResults.value = 0;
  activeIndex.value = -1;

  const { $posthog } = useNuxtApp();
  $posthog?.capture('product_search_autocomplete_selected', {
    product_id: productId(selected),
    query: selectedQuery,
  });

  await navigateTo(productPath(selected));
}

function handleEnter() {
  if (hits.value.length) {
    void selectHit();
  }
}

function handleClear() {
  requestId += 1;
  search.value = '';
  hits.value = [];
  currentPage.value = 1;
  totalPages.value = 0;
  totalResults.value = 0;
  activeIndex.value = -1;
  error.value = false;
  loading.value = false;
}

function handlePageChange(page: number) {
  const nextPage = Math.max(1, Math.min(page, visibleTotalPages.value || 1));

  if (nextPage === currentPage.value) {
    return;
  }

  requestId += 1;
  currentPage.value = nextPage;
  activeIndex.value = -1;
  void runSearch();
}
</script>

<template>
  <div ref="root" class="search-row" :class="{ 'search-row--open': isOpen }">
    <AppSearchField
      v-model="search"
      placeholder="Search products or merchants"
      clearable
      autocomplete="off"
      role="combobox"
      :aria-expanded="isOpen"
      aria-controls="marketplace-search-results"
      @focus="focused = true"
      @keydown.down.prevent="moveActive(1)"
      @keydown.up.prevent="moveActive(-1)"
      @keydown.enter.prevent="handleEnter"
      @keydown.esc.prevent="focused = false"
      @click:clear="handleClear"
    />

    <Teleport to="body">
      <div
        v-if="isOpen"
        id="marketplace-search-results"
        ref="panel"
        class="search-row__panel"
        :class="{ 'search-row__panel--mobile': isMobile }"
        role="listbox"
      >
        <div v-if="loading" class="search-row__state">
          <v-progress-circular indeterminate color="primary" size="18" width="2" />
          <span>Searching...</span>
        </div>

        <div v-else-if="error" class="search-row__state search-row__state--muted">Search is unavailable</div>

        <div v-else-if="hits.length" class="search-row__results">
          <div class="search-row__toolbar">
            <div class="search-row__summary">{{ resultSummary }}</div>

            <div v-if="showPagination" class="search-row__pagination">
              <v-pagination
                :model-value="currentPage"
                :length="visibleTotalPages"
                :total-visible="isMobile ? 5 : SEARCH_VISIBLE_PAGE_BUTTONS"
                rounded="circle"
                density="comfortable"
                @update:model-value="handlePageChange"
              />
            </div>
          </div>

          <div class="search-row__grid">
            <NuxtLink
              v-for="(item, index) in hits"
              :key="item.objectID || productId(item)"
              class="search-row__result"
              :class="{ 'search-row__result--active': index === activeIndex }"
              :to="productPath(item)"
              role="option"
              :aria-selected="index === activeIndex"
              @mouseenter="activeIndex = index"
              @click.prevent="selectHit(item)"
            >
              <span class="search-row__media">
                <v-img class="search-row__image" :src="productImage(item)" cover :aspect-ratio="1" />
              </span>

              <span class="search-row__details">
                <span class="search-row__merchant">
                  <v-icon class="search-row__merchant-icon" icon="mdi-store-outline" size="14" />
                  <span class="search-row__merchant-text">
                    <span
                      v-for="(part, partIndex) in highlightedParts(item.merchant_name || 'Merchant unavailable')"
                      :key="`merchant-${partIndex}`"
                      :class="{ 'search-row__highlight': part.highlighted }"
                    >
                      {{ part.text }}
                    </span>
                  </span>
                </span>
                <span class="search-row__title">
                  <span
                    v-for="(part, partIndex) in highlightedParts(item.title || 'Untitled product')"
                    :key="`title-${partIndex}`"
                    :class="{ 'search-row__highlight': part.highlighted }"
                  >
                    {{ part.text }}
                  </span>
                </span>
                <span v-if="item.category_label" class="search-row__category">
                  <span
                    v-for="(part, partIndex) in highlightedParts(item.category_label)"
                    :key="`category-${partIndex}`"
                    :class="{ 'search-row__highlight': part.highlighted }"
                  >
                    {{ part.text }}
                  </span>
                </span>
                <span class="search-row__bottom">
                  <span v-if="item.sku" class="search-row__sku">
                    <span>SKU: </span>
                    <span
                      v-for="(part, partIndex) in highlightedParts(item.sku)"
                      :key="`sku-${partIndex}`"
                      :class="{ 'search-row__highlight': part.highlighted }"
                    >
                      {{ part.text }}
                    </span>
                  </span>
                  <strong v-if="productPrice(item)" class="search-row__price">{{ productPrice(item) }}</strong>
                </span>
              </span>
            </NuxtLink>
          </div>
        </div>

        <div v-else-if="hasQuery" class="search-row__state search-row__state--muted">No matching products</div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss">
#marketplace-search-results {
  z-index: 1300;
}

.search-row {
  --search-row-panel-top: 64px;
  --search-row-site-width: calc(1136px + 16px * 2);

  position: relative;
  z-index: 1;
  width: 100%;
  max-width: none;

  .search-field {
    .v-field {
      border: 1.5px solid rgb(var(--v-theme-stroke));
      border-radius: 36px !important;
      transition: border-color 0.16s ease;

      &.v-field--focused {
        border-color: rgb(var(--v-theme-primary));
      }
    }
  }

  &__panel {
    --search-row-panel-top: 64px;
    --search-row-site-width: calc(1136px + 16px * 2);

    position: fixed;
    z-index: 1300;
    top: var(--search-row-panel-top);
    left: 50%;
    width: 100vw;
    height: calc(100vh - var(--search-row-panel-top));
    height: calc(100dvh - var(--search-row-panel-top));
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    transform: translateX(-50%);
    border-top: 0.5px solid rgb(var(--v-theme-stroke));
    background-color: rgb(var(--v-theme-background));
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.18);

    &--mobile {
      --search-row-panel-top: 114px;
    }
  }

  &__results {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 18px;
    width: 100%;
    max-width: var(--search-row-site-width);
    margin: 0 auto;
    padding: 20px;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 0;
  }

  &__summary {
    min-width: 0;
    color: rgb(var(--v-theme--secondary));
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    align-content: start;
    gap: 16px;
  }

  &__pagination {
    display: flex;
    flex-shrink: 0;
    justify-content: flex-end;
  }

  &__result {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
    padding: 12px;
    border: 0.5px solid rgb(var(--v-theme-stroke));
    border-radius: 8px;
    background-color: rgb(var(--v-theme-surface));
    color: inherit;
    text-decoration: none;
    transition:
      border-color 0.16s ease,
      background-color 0.16s ease,
      transform 0.16s ease;

    &:hover,
    &--active {
      border-color: rgba(var(--v-theme-primary), 0.55);
      background-color: rgb(var(--v-theme-neutral));
      transform: translateY(-1px);
    }
  }

  &__media {
    position: relative;
    display: block;
    overflow: hidden;
    border-radius: 8px;
    background-color: rgb(var(--v-theme-neutral));
  }

  &__image {
    width: 100%;
    border: 0.5px solid rgb(var(--v-theme-stroke));
    border-radius: 8px;
    background-color: rgb(var(--v-theme-neutral));
  }

  &__details {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: 4px;
  }

  &__merchant {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    overflow: hidden;
    color: rgb(var(--v-theme--tertiary));
    font-size: 13px;
    line-height: 1.25;
  }

  &__merchant-icon {
    flex-shrink: 0;
    color: rgb(var(--v-theme--tertiary));
  }

  &__merchant-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__category {
    display: inline-flex;
    align-self: flex-start;
    max-width: 100%;
    overflow: hidden;
    padding: 2px 8px;
    border: 0.5px solid rgb(var(--v-theme-stroke));
    border-radius: 999px;
    background-color: rgb(var(--v-theme-neutral));
    color: rgb(var(--v-theme--secondary));
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sku {
    min-width: 0;
    overflow: hidden;
    color: rgb(var(--v-theme--tertiary));
    font-size: 12px;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__title {
    display: -webkit-box;
    min-height: 36px;
    overflow: hidden;
    color: rgb(var(--v-theme--primary));
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  &__highlight {
    border-radius: 3px;
    background-color: rgba(var(--v-theme-primary), 0.16);
    color: rgb(var(--v-theme-primary));
    font-weight: 700;
  }

  &__bottom {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
    margin-top: auto;
    padding-top: 4px;
  }

  &__price {
    flex-shrink: 0;
    margin-left: auto;
    color: rgb(var(--v-theme-primary));
    font-size: 18px;
    font-weight: 700;
    line-height: 1.2;
    text-align: right;
    white-space: nowrap;
  }

  &__state {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    max-width: var(--search-row-site-width);
    height: 100%;
    min-height: 56px;
    margin: 0 auto;
    padding: 12px;
    color: rgb(var(--v-theme--secondary));
    font-size: 14px;
  }

  &__state--muted {
    color: rgb(var(--v-theme--tertiary));
  }
}

.search-row--open {
  z-index: 1300;
}

.search-row.search-mobile {
  max-width: none;
}

.search-row__panel--mobile {
  .search-row__results,
  .search-row__state {
    padding: 12px 16px 16px;
  }
}

@media (max-width: 520px) {
  .search-row {
    &__toolbar {
      align-items: flex-start;
      flex-direction: column;
    }

    &__pagination {
      justify-content: flex-start;
      width: 100%;
    }

    &__grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    &__result {
      padding: 10px;
    }
  }
}

@media (max-width: 380px) {
  .search-row {
    &__grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
</style>
